/**
 * my.dedupr.cloud — the tenant application shell, and (since ADR-069) the
 * platform's one auth gateway (ADR-065).
 *
 * Composes the catalogue's `AppShell`, which means this app gets 08 §2's
 * documented mobile pattern (a true bottom sheet) rather than `apps/settings`'
 * desktop-rail-at-every-width compromise — the thing TODO B-50 was opened for.
 *
 * Hash routing again, for the same reason as `apps/settings`: served as static
 * assets by Workers Assets, so a hash needs no rewrite rule that can be
 * forgotten and produce a 404 on refresh.
 *
 * ── The auth gateway, in outline ─────────────────────────────────────────
 * Everyone — staff and customers — signs in HERE, against the one Supabase
 * project, via `@dedupr/session`'s `GatewayAuth` (an `HttpOnly`,
 * `Domain=.dedupr.cloud` refresh-token cookie — never `sessionStorage`, so
 * the session now genuinely crosses subdomains). The session resolves which
 * of ADR-065's four destinations a visitor belongs at: an operator is sent to
 * `admin.dedupr.cloud`; a Managed Cloud tenant (or one with no deployment
 * record yet — F26's setup journey) stays right here; a dedicated-enterprise
 * client is sent to their own hostname (rendered as an honest placeholder
 * until that hostname column exists); an Entry/Sandbox tenant is offered the
 * PWA. `apps/settings` no longer runs an independent sign-in of its own — an
 * unauthenticated visit there redirects here, with `?return_to=` honoured
 * ahead of the ordinary routing so a settings-bound visitor lands back where
 * they wanted.
 */
import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { setAnalyticsContext } from './analytics.js';
import { AppShell, AvatarMenu, Badge, BrandMark, Callout, ChoiceTile, FormattingProvider, Icon, IconButton, MENU_GLYPHS, Modal, NotificationTray, OrgSwitcher, Placeholder, SearchField, Spinner, StatusPill, Tooltip, type SidebarItem } from '@dedupr/ui';
import type { GatewaySession, RouteDestination } from '@dedupr/session';
import { gateAfterRefreshFailure } from './gateOutcome.js';
import {
  gatewayAuth,
  isLive as gatewayIsLive,
  resolveAfterAuth,
  safeReturnTo,
  startTrialAndRoute,
  surfaces,
} from './auth.js';
/* `isUnlistedRoute` is kept: `/local` and `/gallery` own their own heading and belong to no nav item,
   so without it `activeRoute`'s fallback renders "Dashboard" as their h1 and highlights Dashboard in
   the sidebar — the page then announces a screen the visitor is not on. */
import { ACCOUNT_MENU_ROUTES, NAV, SUBNAV, isUnlistedRoute, routeFromHash, type NavRoute } from './nav.js';
import { ASSISTANT_PROMPTS, HELP } from './help.js';
import { Matches } from './screens/Matches.js';
import { HostedMatches } from './screens/HostedMatches.js';
import { Findings } from './screens/Findings.js';
import { HostedFindings } from './screens/HostedFindings.js';
import { Actions } from './screens/Actions.js';
import { Destinations } from './screens/Destinations.js';
import { Reports } from './screens/Reports.js';
import { Compliance } from './screens/Compliance.js';
import { LocalRun } from './screens/LocalRun.js';
import { CustomObjectsRoute } from './screens/CustomObjectsRoute.js';
import { Administration } from './screens/Administration.js';
import { LocalAdministration } from './screens/LocalAdministration.js';
import { LocalAuditLog } from './screens/LocalAuditLog.js';
import { AuditLog } from './screens/AuditLog.js';
import { Quality } from './screens/Quality.js';
import { Value } from './screens/Value.js';
import { Contracts } from './screens/Contracts.js';
import { Surfaces } from './screens/Surfaces.js';
import { Remediation } from './screens/Remediation.js';
import { LocalQuality } from './screens/LocalQuality.js';
import { Sources } from './screens/Sources.js';
import { Dashboard } from './screens/Dashboard.js';
import { StartTrial } from './screens/StartTrial.js';
/**
 * ⚠️ Lazily imported, and that is the whole point (B-96).
 *
 * A static import put all 994 lines of the review gallery — plus every component it
 * demonstrates — into the bundle every CUSTOMER downloads, for a surface reached only by
 * typing a URL. `React.lazy` moves it to its own chunk that is fetched when someone
 * actually visits `#/gallery`, so it costs a customer nothing.
 *
 * Not deleted, and not moved behind a build flag: it is a genuinely useful review surface,
 * and a flag would mean the thing we review is not the thing we ship.
 */
const Gallery = lazy(() => import('./gallery.js').then((m) => ({ default: m.Gallery })));
import './App.css';
import {
  api,
  ErrorState,
  GatewaySignIn,
  ScenarioSwitcher,
  useHelpDock,
  useQuery,
  type SignUpDetails,
  type TrialSignupDetails,
} from '@dedupr/app-kit';
import { useTurnstile } from '@dedupr/turnstile';
import { hasRequestedTrial, markTrialRequested } from './trialMarker.js';
import { canInstall, isInstalled, promptInstall } from './pwa.js';
/* Extracted so its retry, motion fallback and focus re-check are testable — none of the three can
   be observed reliably in a background browser pane, which is exactly why they need tests. */
import { revealSection } from './revealSection.js';
import { carriesUnusableCredential } from './unusableCredential.js';

/** Screens with no implementation yet, each naming its owner and blocker. */
const PLACEHOLDERS: Record<string, { title: string; description: string; owner: string; blockedOn: string }> = {
  /* ⚠️ Kept for a CLOUD client, and removed for a local one by the route above. Z-3 built the local
     findings feed; the hosted one still needs M3/M7, and a placeholder that says so is honest where
     rendering local findings to a cloud client would not be. */
  '/findings': {
    title: 'Findings',
    description:
      'The ranked feed of next steps, each with its affected record count and — where a rate is configured — a monetary estimate that always discloses its formula.',
    owner: 'F08 / M7',
    blockedOn: 'The findings pipeline (M3/M7). The contract models findings; nothing computes them yet.',
  },
  '/actions': {
    title: 'Proposed actions',
    description:
      'Action sets awaiting approval, with the safety chain made visible: dry-run diff, sample-first, verify, then apply — and rollback inside the window.',
    owner: 'F04 / M6',
    blockedOn: 'The action executor (M6) and the SafetyGate catalogue entry.',
  },
  '/destinations': {
    title: 'Destinations',
    description:
      'Export targets with their verification state, plus per-column PII handling that must be set explicitly rather than defaulted.',
    owner: 'F09 / M8',
    blockedOn: 'The export pipeline (M8). Credential capture is built — see the register on the screen itself.',
  },
  '/reports': {
    title: 'Reports',
    description:
      'Downloadable report artifacts and the savings summary, showing estimate against actual.',
    owner: 'F08 / M7',
    blockedOn: 'M7 report generation.',
  },
};

/** The ADR-065 gate state — everything before the ordinary dashboard shell renders. */
type Gate =
  | { kind: 'checking' }
  | { kind: 'signed_out' }
  | { kind: 'stay' }
  /** A redirect (`window.location.assign`) is already in flight; render nothing more. */
  | { kind: 'redirecting' }
  | { kind: 'dedicated_unresolved' }
  | { kind: 'pwa' }
  /**
   * ⚠️ **The session question could not be ASKED** — distinct from `signed_out`, which is the edge
   * answering it. See `GatewayUnreachable`: an offline `fetch` rejects, and this state exists so that
   * rejection resolves the gate instead of leaving it on `checking` for ever (V-3).
   */
  | { kind: 'unreachable' }
  /** Signed in, belongs to no organisation — F26's self-serve sign-up (see `StartTrial`). */
  | { kind: 'onboard'; session: GatewaySession };

/** One row of M22's store, as `/v1/notifications` returns it (F14-C). */
interface TrayItem {
  id: string;
  kind: string;
  title: string;
  body: string;
  path: string | null;
  needs_you: boolean;
  read: boolean;
  occurred_at: string;
}

export function App() {
  /**
   * ADR-065/069: `my.dedupr.cloud` is the platform's one auth gateway. In
   * mock mode (`gatewayIsLive === false`) this starts and stays at `'stay'`
   * so the ordinary dashboard renders immediately — the same "never gate
   * mock data behind a fake sign-in" rule `apps/settings`/`apps/admin`
   * already apply.
   */
  const [gate, setGate] = useState<Gate>(() => (gatewayIsLive ? { kind: 'checking' } : { kind: 'stay' }));

  /* The "I am human" widget for the trial form. Held here rather than inside `GatewaySignIn`,
     because that component is shared and does no network access of its own — the same slot pattern
     the help dock already uses. */
  /**
   * The visitor arrived carrying a credential this app cannot use.
   *
   * ⚠️ Read once, on mount: it is a property of how they got here, and re-reading it after the hash
   * changes would resurface the message while they are typing. `#access_token=` is Supabase's
   * implicit flow — issued when the link request did not originate in this browser.
   */
  const [unusableCredential] = useState(
    () => typeof window !== 'undefined' && carriesUnusableCredential(window.location.hash)
  );

  const turnstile = useTurnstile();

  /**
   * Decide where a freshly-authenticated (or freshly-returning) session
   * belongs. A `?return_to=` wins over the ordinary ADR-065 routing — it
   * means a visitor arrived here FROM another app (e.g. `settings.` gating
   * an unauthenticated visit) and wants to go back, not into this
   * dashboard.
   */
  const routeSession = useCallback(async (session: GatewaySession) => {
    const returnTo = safeReturnTo(new URLSearchParams(window.location.search).get('return_to'));
    if (returnTo) {
      setGate({ kind: 'redirecting' });
      window.location.assign(returnTo);
      return;
    }

    const destination: RouteDestination = await resolveAfterAuth(session);
    if (destination.kind === 'admin') {
      setGate({ kind: 'redirecting' });
      window.location.assign(`${surfaces.admin}/`);
      return;
    }
    if (destination.kind === 'dedicated') {
      setGate({ kind: 'redirecting' });
      window.location.assign(`https://${destination.hostname}/`);
      return;
    }
    if (destination.kind === 'dedicated_unresolved') {
      setGate({ kind: 'dedicated_unresolved' });
      return;
    }
    if (destination.kind === 'pwa') {
      setGate({ kind: 'pwa' });
      return;
    }
    if (destination.kind === 'onboard') {
      /* The session is carried in the gate state rather than re-read: `startTrial` needs this
         exact access token, and `gatewayAuth.session()` could have been refreshed out from under
         us between the resolve and the submit. */
      setGate({ kind: 'onboard', session });
      return;
    }
    setGate({ kind: 'stay' });
  }, []);

  /**
   * Create the organisation and its trial, then go wherever that now routes to.
   *
   * ⚠️ Deliberately re-runs the full routing resolution instead of assuming `stay`: an operator
   * may have provisioned this client while the tab sat open, in which case the tier decides. Two
   * copies of the routing rule is exactly how a screen ends up disagreeing with the resolver.
   */
  const startTrialFor = useCallback(
    async (session: GatewaySession, details: { orgName: string; region: 'eu' | 'us' }) => {
      const destination = await startTrialAndRoute(session, details);
      if (destination.kind === 'onboard') {
        /* Should be unreachable — the org was just created, so the membership read that produced
           this must have failed. Throwing surfaces it on the form the visitor is looking at,
           rather than silently re-rendering the same screen as if nothing had happened. */
        throw new Error(
          'Your organisation was created, but we could not confirm it. Reload this page to continue.'
        );
      }
      await routeSession(session);
    },
    [routeSession]
  );

  /**
   * Send a sign-up to `/api/signup`.
   *
   * ⚠️ Creates nothing — no org, no user, no row. It emails us. That is honest for a product whose
   * onboarding still needs a person, and it avoids writing a named prospect into the control plane
   * before [B-53](../../../docs/platform/TODO.md) has settled the lawful basis for holding one there.
   *
   * A `not_configured` answer is turned into the Worker's own detail text, which names the
   * `mailto:` to use instead — the same honest degradation `apps/landing`'s audit form already
   * relies on. A form that silently swallows a submission is worse than one that says "email us".
   */
  const submitSignUp = useCallback(async (details: SignUpDetails) => {
    const response = await fetch('/api/signup', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(details),
    });
    if (response.ok) {
      /* ⚠️ Marks the browser too (Z-9). This is the *sales* form rather than the trial one, but it
         asks the same question — who is evaluating this — and answering it is what earns the local
         product. Without this line the `signup_sent` screen would offer "Try it now" and the route
         would bounce the person straight back to the sign-in card they just filled in. */
      markTrialRequested();
      return;
    }
    const problem = (await response.json().catch(() => null)) as { detail?: string } | null;
    throw new Error(problem?.detail ?? 'That could not be sent. Write to hello@dedupr.cloud instead.');
  }, []);

  /**
   * Submit the trial sign-up to our own Worker.
   *
   * ⚠️ **Goes through a Worker, and that is not a contradiction of "trials cost us nothing".** The
   * trial itself runs entirely in the visitor's browser — a thousand trials cost us nothing, which
   * is the whole point of the PWA. This runs once per signup, for a few milliseconds, and it is the
   * only place that can verify Turnstile (the secret cannot reach a browser) and carry the company
   * onto the magic link as user metadata.
   *
   * `202` is not a failure: Supabase's own auth rate limit deferred the email, the lead is captured
   * either way, and the screen says "queued" rather than "something went wrong".
   */
  const startTrialSignup = useCallback(async (details: TrialSignupDetails): Promise<'sent' | 'queued'> => {
    const response = await fetch('/api/trial-signup', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(details),
    });
    const body = (await response.json().catch(() => null)) as { code?: string; detail?: string } | null;

    if (response.status === 202 && body?.code === 'high_demand') {
      markTrialRequested();
      return 'queued';
    }
    if (response.ok) {
      markTrialRequested();
      return 'sent';
    }
    /* The Worker's own message, which names whether the problem is the address, the human check, or
       our own configuration. Replacing it with a generic failure would discard the only actionable
       part — and for `email_not_business` it is the sentence that offers the way through. */
    throw new Error(body?.detail ?? 'That could not be sent. Write to hello@dedupr.cloud instead.');
  }, []);

  /* The bootstrap check — the ONE call that answers "am I signed in", backed
     by the HttpOnly cross-subdomain cookie rather than any client-readable
     storage (ADR-069). Mount-only: this is a one-time check, not something to
     repeat on every `routeSession` identity change. */
  useEffect(() => {
    if (!gatewayIsLive || !gatewayAuth) return;
    let live = true;

    const check = () => {
      gatewayAuth!
        .refresh()
        .then((session) => {
          if (!live) return;
          if (!session) {
            setGate({ kind: 'signed_out' });
            return;
          }
          void routeSession(session);
        })
        .catch((e) => {
          if (!live) return;
          /**
           * ⚠️ **This `catch` is the fix for V-3, and its absence was a shipped defect in the one
           * scenario the PWA exists for.** `refresh()` rejects when the edge cannot be reached — and
           * the service worker deliberately never caches `/api/*` — so launching the installed app
           * with no connection left the gate on `checking` and rendered "Checking your session" for
           * ever. The manifest's `start_url` is `/` — the installed app opens on the Dashboard, the
           * same screen a cloud client lands on (owner decision, 2026-08-06); the engine chunk is pre-cached and the
           * records are in IndexedDB: everything needed was present, behind a question we could not
           * ask and did not need answered.
           *
           * The decision itself lives in `gateAfterRefreshFailure` because this branch is unreachable
           * in mock mode — see that module for why a rule nothing can assert is a rule that rots.
           */
          setGate({ kind: gateAfterRefreshFailure(e) });
        });
    };

    check();

    /* ⚠️ Retry when the browser regains connectivity, so a launch that happened to be offline heals
       itself rather than needing a manual reload. Cheap, and without it `unreachable` would outlive
       the condition that caused it — the same "nothing retried, so the error outlived the fix" defect
       recorded for the session query (X-7). */
    const onOnline = () => {
      if (!live) return;
      setGate({ kind: 'checking' });
      check();
    };
    window.addEventListener('online', onOnline);

    return () => {
      live = false;
      window.removeEventListener('online', onOnline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [path, setPath] = useState(() => routeFromHash(window.location.hash));
  const [dataNonce, setDataNonce] = useState(0);
  /* Search query is local state only. The field is the prototype's topbar shape;
     the index and result popover are F29 backlog, and wiring this to a fake
     result list would be worse than a field that visibly does nothing yet. */
  const [query, setQuery] = useState('');
  /* Questions asked of the assistant. Held locally and unanswered on purpose —
     see the Assistant's `onSubmit` note below. */
  const [modeModalOpen, setModeModalOpen] = useState(false);
  const [orgIndex, setOrgIndex] = useState(0);

  /**
   * ⚠️ **Keyed on `gate.kind`, and that is what makes the token available.**
   *
   * This ran once on mount with `[dataNonce]` alone — while the gate was still `checking` and no
   * gateway session existed. `accessToken()` therefore returned null, `/v1/session` answered 401,
   * and the shell rendered *"something went wrong on our side"*. Nothing retried, so the error
   * outlived the sign-in that would have fixed it: the API was right, the request was unauthenticated,
   * and the screen blamed the server.
   *
   * Including `gate.kind` re-runs the query the moment the gate resolves, by which point
   * `accessToken()` has a token to hand over. Found by calling `/v1/` for real — in mock mode the
   * request is intercepted before any header is inspected, so no test could see it.
   */
  const session = useQuery(() => api.getSession(), [dataNonce, gate.kind]);
  const memberships = session.data?.memberships ?? [];
  const active = memberships[orgIndex] ?? memberships[0];
  const orgId = active?.org_id;

  /* Search results — the prototype's dropdown (kind tag · label · mono meta).
     It searches what the app can actually reach today: screens (nav + sub-nav)
     and connected sources from the API. Records/findings/actions join as their
     endpoints gain search (F29's index question); fabricating rows for them
     here would be inventing data. */
  /**
   * Whether the product runs **on this device** for this visitor.
   *
   * ⚠️ **A tier fact, not "has no organisation" — and conflating the two was a live defect.** An
   * Entry/Sandbox client (ADR-065's `pwa` destination, `deployment_record.tier = 'entry_sandbox'`)
   * has a real org and a real `orgId`, so every screen gated on `!orgId` showed them the CLOUD
   * variant: placeholders where their working screens are, and a Dashboard querying a data plane
   * nobody provisioned for them. The absence of an org is a *second*, separate reason to run
   * locally — a visitor trying the product before signing up — and both belong here rather than
   * being re-derived per screen.
   *
   * A failed session read does NOT land here: the `session.status === 'error'` branch above returns
   * an ErrorState first, because "we could not find out" must never be answered with "load a
   * spreadsheet" (the Z-3 near-miss, pinned by tests in two files).
   */
  /* ⚠️ `session.status !== 'error'` is load-bearing, and its absence was visible live.
     `orgId` comes from `/v1/session`. When that call FAILS, `orgId` is undefined for a reason that
     has nothing to do with tiers — so the first version rendered the local nav and a mode pill
     reading "Local (PWA) — Running on this device" **beside** a Dashboard saying the data service
     was unreachable. The chrome claimed one thing and the content another, and the chrome was
     guessing. Observed with a freshly-onboarded client (org, no deployment record → `stay`) while
     `/v1/` was not running.
     This is the Z-3 trap in a new place: not-knowing must never be answered with "local". The tier
     fact (`gate.kind === 'pwa'`) still stands on its own, because it came from the control plane. */
  /**
   * ⚠️ **The third "we know it's local" fact, alongside `gate.kind === 'pwa'` — found live, not in
   * review.** A signed-out visitor who has submitted the trial form (`hasRequestedTrial()`) is ALSO
   * a known-local case, for the identical reason `gate.kind === 'pwa'` is: nothing here is a guess.
   * Before this, `localMode` only became true once `/v1/session` had been *asked and answered* with
   * "no org" — so the pre-account trial preview (Z-9's "Try it now") fell into the Z-3 branch instead:
   * `session.status === 'error'` (no cookie yet, correctly), `gate.kind` is `'signed_out'` not `'pwa'`,
   * so `localMode` came out FALSE. The visitor landed on a bare, shell-less `<LocalRun/>` — no nav, no
   * mode pill, none of "Load a file"/Sources/Findings/Actions/Destinations/Reports — while an
   * *actually* signed-in Entry/Sandbox client sees the full shell for the exact same on-device product.
   * Same product, two different screens, for no reason a visitor could see.
   */
  /**
   * ⚠️ **Local mode is decided by BEING INSTALLED, never by a URL** — owner decision, 2026-08-06.
   *
   * Before this the tier was chosen by a hash path: `hasRequestedTrial() && path === '/local'` set a
   * ref, and `/local` was a route in the web nav. So **the web carried a link labelled local**, and a
   * visitor ran the on-device product in an ordinary browser tab. The decision removes that: local
   * mode is the **installable PWA and nothing else** — same UI and UX as cloud, minus only what a
   * browser genuinely cannot do.
   *
   * Three facts make it true, and each is *known* rather than inferred:
   *
   *   1. `isInstalled()` — this window is running as an installed app (`display-mode: standalone`, or
   *      iOS's `navigator.standalone`). The primary fact now.
   *   2. `gate.kind === 'pwa'` — the **control plane** said this client's tier is Entry/Sandbox. Still
   *      load-bearing: a signed-in Entry/Sandbox client is local whatever the display mode reports.
   *   3. A signed-in session that resolved to **no org** — mid-onboarding, no hosted plane to read.
   *
   * ⚠️ **What this deliberately removes: try-before-install.** A visitor can no longer load a CSV in a
   * web tab. That is a funnel change rather than a refactor, and it is the owner's stated decision —
   * the landing page's job becomes *install the app*, and the first run happens inside it.
   * `InstallOffer` is the affordance that replaces the old `#/local` link.
   *
   * ⚠️ **Read ONCE into a ref, not per render.** `matchMedia('(display-mode: standalone)')` can change
   * mid-session, and a tier that flipped underneath a running session would swap every screen's data
   * source — the shape of the defect this replaces, not a fix for it.
   */
  const installedRef = useRef<boolean | null>(null);
  if (installedRef.current === null) installedRef.current = isInstalled();
  const runningInstalled = installedRef.current === true;
  const localMode = runningInstalled || gate.kind === 'pwa' || (session.status !== 'error' && !orgId);

  /**
   * The trial's own state, for the banner below — **V-22, decided 2026-08-04: banner + keep it
   * usable.**
   *
   * Nothing in this app read `subscription` at all, so an expired Entry/Sandbox trial kept full use
   * silently — which made the 7-day number on the signup screen copy rather than behaviour, by
   * accident rather than decision. The decision: the product keeps working (the data is theirs, on
   * their device, and the engine runs there at zero cost to us — holding it hostage would contradict
   * F13's own never-delete posture), and a persistent banner names the state and the way to convert.
   *
   * Keys on `state === 'read_only'`/`'canceled'` — the states the nightly sweep and a cancellation
   * actually produce — rather than comparing `trial_ends_at` to the device clock, which is the
   * untrusted-clock mistake ADR-073 exists to avoid, one layer up. The cost is honesty about a small
   * window: between expiry and the nightly sweep the banner is absent, which is the sweep's cadence
   * being the truth rather than a defect here.
   *
   * Only for the Entry/Sandbox tier (`gate.kind === 'pwa'`): a cloud client's billing surface is
   * `settings.`, and duplicating its messaging here would be two owners for one fact.
   */
  const trialState = useQuery(
    () =>
      gate.kind === 'pwa' && orgId
        ? api.getSubscription(orgId).then((sub) => sub.state)
        : Promise.resolve(null),
    [gate.kind, orgId]
  );
  const trialEnded = trialState.data === 'read_only' || trialState.data === 'canceled';

  const sources = useQuery(
    /* `localMode` too: an Entry/Sandbox client's org id is real but its data plane is not, so this
       would search a service that answers `not_provisioned` for them on every keystroke. */
    () => (orgId && !localMode ? api.listSources({ org_id: orgId }) : Promise.resolve(null)),
    [orgId, localMode, dataNonce]
  );

  useEffect(() => {
    const onHash = () => setPath(routeFromHash(window.location.hash));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const navigate = useCallback((next: string) => {
    window.location.hash = next;
  }, []);

  // Badge counts, per 08 §2's specified sources. Fetched together and only once
  // an org is known — a badge query without org_id would (correctly) be refused
  // as `org_id_required` by the API.
  const badges = useQuery(async () => {
    /* ⚠️ `localMode`, not just `!orgId`: an Entry/Sandbox client HAS an org id, and firing these
       against a data plane that was never provisioned for them yields five failed requests per
       page load and five badges that are absent for the wrong reason. */
    if (!orgId || localMode) return {} as Record<string, number>;
    const entries = await Promise.all(
      NAV.filter((r) => r.badge).map(async (r) => {
        try {
          return [r.id, await r.badge!(api, orgId)] as const;
        } catch {
          // A failed badge must never take the shell down. Absent is better than
          // wrong: a badge showing 0 because a query failed would say
          // "nothing needs attention", which is the opposite of the truth.
          return [r.id, -1] as const;
        }
      })
    );
    return Object.fromEntries(entries) as Record<string, number>;
  }, [orgId, localMode, dataNonce]);

  /* ⚠️ **The nav is now IDENTICAL in both modes** — owner decision, 2026-08-06: local has the same UI
     and UX as cloud. Z-8 had injected a local-only "Load a file" entry pointing at `/local`, which was
     the right fix for the problem it had (the one working feature was reachable only by typing a URL)
     and the wrong shape for this one: it made local a *different* product with a different nav.
     Adding a file is now what `Sources` does in local mode — the same screen where a cloud client
     connects a source, which is the same entry action expressed the same way. */
  const navRoutes: NavRoute[] = NAV;

  const items: SidebarItem[] = navRoutes.map((r) => {
    const count = badges.data?.[r.id];
    return {
      id: r.id,
      label: r.label,
      // The Icon component is aria-hidden by default; the nav item supplies the
      // accessible name, so passing a title here would double-announce it.
      icon: <Icon name={r.icon} />,
      // -1 is the sentinel for "query failed"; omit rather than show a wrong 0.
      badgeCount: count != null && count > 0 ? count : undefined,
      badgeLabel: r.badgeLabel,
      // Inline sub-navigation for whichever section is active — the prototype's
      // own SUBNAV table. The Sidebar has supported this since it was built; no
      // nav row had ever declared any, so it never rendered.
      children: SUBNAV[r.id]?.map((sub) => ({
        id: sub.id,
        label: sub.label,
        onSelect: () => {
          navigate(sub.route);
          revealSection(sub.section);
        },
      })),
      onSelect: () =>
        r.externalHost ? window.location.assign(r.externalHost) : navigate(r.id),
    };
  });

  const activeRoute = navRoutes.find((r) => r.id === path) ?? NAV[0]!;
  /* An unlisted route (`/local`, `/gallery`) is not a nav destination: it owns its own heading
     and belongs to no nav item. Without this, `activeRoute`'s fallback to NAV[0] rendered
     "Dashboard" as their h1 AND highlighted Dashboard in the sidebar — so the page announced
     itself as a different page. Found on `/local`; it was already true of `/gallery`. */
  const unlisted = isUnlistedRoute(path);
  /* Split from `unlisted` for Z-8: `/local` joins the local-mode nav, so the sidebar should
     highlight it — but `LocalRun` renders its own `PageHeader` h1, so the shell must still NOT add
     a second one. The duplicated-h1 defect is in this repo's history; the two concerns are
     different questions and conflating them is how it comes back. */
  const activeNavId = navRoutes.some((r) => r.id === path) ? path : unlisted ? '' : activeRoute.id;

  /* One shared help dock, identical in all three product apps. */
  const help = useHelpDock({
    topics: HELP,
    prompts: ASSISTANT_PROMPTS,
    routeKey: activeRoute.id,
    surface: 'tenant',
    intro:
      'I can explain anything on screen, or help you decide. Ask me why a pair scored the way it did, or what a finding would cost you to ignore.',
  });

  /* The account menu, built from the shared route table so it cannot drift from
     the primary nav's spelling. Every destination is a real route; the ones whose
     screens are not built yet land on their placeholder, which states its owner
     and blocker — an honest arrival beats a menu that quietly omits half the
     product. */
  const accountMenu = [
    {
      label: 'Your account',
      items: ACCOUNT_MENU_ROUTES.account.map((i) => ({
        id: i.id,
        label: i.label,
        glyph: i.glyph,
        hint: 'hint' in i ? i.hint : undefined,
        href: i.href,
      })),
    },
    {
      label: 'Platform settings',
      items: ACCOUNT_MENU_ROUTES.platform.map((i) => ({
        id: i.id,
        label: i.label,
        glyph: i.glyph,
        href: i.href,
      })),
    },
    {
      items: [
        {
          id: 'logout',
          label: 'Log out',
          glyph: MENU_GLYPHS.logout,
          tone: 'danger' as const,
          /* Real now that the gateway session exists (ADR-069) — clears the
             domain-wide cookie, signing out of every `*.dedupr.cloud` app at
             once, then reloads so the sign-in gate re-renders. In mock mode
             `gatewayAuth` is null and this menu item is inert, same as
             before. */
          onSelect: gatewayAuth
            ? () => void gatewayAuth!.signOut().then(() => window.location.reload())
            : undefined,
        },
      ],
    },
  ];

  /* ── F15-C: the analytics seam learns who and where, in ONE place ────────────────────────────
     ⚠️ Derived from the same `localMode` fact the nav and the mode pill read, so the three cannot
     disagree — which is how a previous drift (X-4) happened. A signed-out or pre-onboarding visitor
     has `orgId` undefined, and the seam emits nothing for them by design. */
  useEffect(() => {
    setAnalyticsContext({ syncMode: localMode ? 'local_pwa' : 'cloud', orgId: orgId ?? null });
  }, [localMode, orgId]);

  /* ── F14-C: the tray reads M22's real store ─────────────────────────────────────────────────
     ⚠️ **This replaced an invented feed.** Until the store existed the tray rendered ONE derived item
     — a source needing attention, from the same badge query the nav uses — because a fabricated feed
     would have been worse than an honest empty state. It is now the six events the email layer sends,
     written by the same call, so the two channels cannot disagree.
     ⚠️ **`localMode` reads nothing**, like the badges below it: an Entry/Sandbox client has an org id
     and no provisioned plane, so this would be one more failed request per page load. */
  const [trayNonce, setTrayNonce] = useState(0);
  const tray = useQuery(async () => {
    if (!orgId || localMode) return { notifications: [] as TrayItem[], unread: 0 };
    const response = await fetch(`/v1/notifications?org_id=${encodeURIComponent(orgId)}`, {
      headers: { accept: 'application/json' },
    });
    if (!response.ok) throw new Error(String(response.status));
    return (await response.json()) as { notifications: TrayItem[] | null; unread: number | null };
  }, [orgId ?? '', localMode, trayNonce]);

  const markRead = async (id: string) => {
    try {
      await fetch(`/v1/notifications?org_id=${encodeURIComponent(orgId ?? '')}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ id }),
      });
    } catch {
      /* ⚠️ Swallowed on purpose: failing to record that you read something must not stop you going
         where it points. The next read simply shows it unread again, which is the harmless direction. */
    }
    setTrayNonce((n) => n + 1);
  };

  const markAllRead = async () => {
    try {
      await fetch(`/v1/notifications?org_id=${encodeURIComponent(orgId ?? '')}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ all: true }),
      });
    } catch {
      /* Same reasoning as `markRead`: a failed acknowledgement is the harmless direction. */
    }
    setTrayNonce((n) => n + 1);
  };

  /* ⚠️ **`null` travels all the way to the tray now (B-164, closed 2026-08-07).** It used to be
     flattened to `[]` here, so an unreadable plane rendered as *"No notifications yet"* — a screen
     saying something it does not know, to exactly the client most likely to have something waiting.
     The tray has a not-known state; this hands it the distinction rather than erasing it. */
  const notifications =
    tray.data?.notifications == null
      ? null
      : tray.data.notifications.map((n) => ({
          id: n.id,
          title: n.title,
          detail: n.body,
          read: n.read,
          needsYou: n.needs_you,
          onSelect: () => {
            void markRead(n.id);
            if (n.path) navigate(n.path.replace(/^\/#/, ''));
          },
        }));

  function renderScreen() {
    /* ⚠️ A failed session no longer blanks the whole app, and that was a real go-live defect found in
       a browser: it returned `ErrorState` for **every** route, including the two that need no session
       at all. Local mode reads a file in this browser and matches it on this device — it has no
       account, no organisation and no API call in it — so "the session request failed" is not a
       reason to refuse it. With no `/v1/` API deployed yet (S3), the session request fails on every
       load, which made the one working part of the product unreachable in production.

       `/sources` is exempt for the same reason at one remove: its connect surface reads the relay's
       own routes rather than `/v1/`, and `ConnectedSourceList` renders nothing without an org — so the
       screen degrades to "here is how to add a source", which is exactly right for a visitor who has
       no account yet. ⚠️ This will need revisiting when the relay becomes session-gated
       ([B-133](../../../docs/platform/TODO.md)); it is consistent with today's model, not with the
       intended one. */
    /* ⚠️ `/matches` joins the no-session list because its data is this device's local run, read from
     IndexedDB. Requiring a session would make the one part of the product that works offline
     unreachable offline — and the route allow-list is exactly where a route gets forgotten (it was
     `#/local` once, before that route was removed)
     already (B-70's trap), silently falling through to the Dashboard. */
    /* ⚠️ **Deliberately NOT extended for `/` or `/findings`, and that was tried and reverted.**
       Z-3 added both, reasoning that a screen reading only IndexedDB needs no session. The suite
       caught the flaw: a session failure means we do NOT KNOW whether this is a cloud client or a
       local one. Treating it as local would show a cloud user "Start with a file" — telling them to
       load a spreadsheet when their actual problem is that they are not authenticated. An error that
       says what happened beats a screen that quietly answers a different question.
       The screens below are here because they genuinely serve one mode only: `/local` and `/matches`
       read the device's own store in both modes, and `/sources` degrades to its connect panel. */
    const needsNoSession =
      /* ⚠️ `/local` removed with the route itself. The comment below still holds: this allow-list is
         exactly where a route gets forgotten, so it names what works offline rather than what does not. */
      path === '/gallery' || path === '/sources' || path.startsWith('/matches');
    /**
     * ⚠️ **An Entry/Sandbox client is exempt, and the distinction is the whole point of the rule
     * above rather than an exception to it.**
     *
     * The rule exists because a failed session read means we do **not know** whether this is a
     * cloud client or a local one, and answering "load a spreadsheet" to someone who is merely
     * unauthenticated is worse than an error. But `gate.kind === 'pwa'` is not a guess: it came
     * from the control plane, which answered, and it says this client's tier is `entry_sandbox`.
     * We know. What failed is `/v1/session` — a **different service**, one this tier's product
     * never touches, because the engine runs on the device.
     *
     * Found by signing in for real against a local Supabase: the badge correctly read
     * "Local (PWA) — Running on this device" while the Dashboard beside it rendered
     * `api_unreachable`. In mock mode `getSession()` always succeeds, so no test could see it.
     *
     * ⚠️ Deliberately `gate.kind === 'pwa'` and **not** `localMode` — `localMode` is true whenever
     * `orgId` is undefined, which is exactly what a failed session read produces. Using it here
     * would re-open the Z-3 trap this rule was written to close.
     *
     * `isTrialPreview` joins it for the same reason: a signed-out visitor who has submitted the
     * trial form is a KNOWN local case too, not an unknown one — `hasRequestedTrial()` came from
     * this browser's own action, not a guess. Without it, every route but the two Z-9 hard-coded
     * (`/local`, `/matches`) answered `ErrorState` for a trial visitor who had done everything
     * asked of them, which is the cut-down, upload-only experience this comment exists to prevent.
     */
    const tierIsLocal = gate.kind === 'pwa' || runningInstalled;
    if (session.status === 'error' && !needsNoSession && !tierIsLocal) {
      return (
        <ErrorState code={session.errorCode} message={session.errorMessage} onRetry={session.reload} />
      );
    }
    // The component gallery (audit T-4.3). Not in NAV: it is a review surface for
    // us, reached by URL, and a nav entry would put it in front of customers.
    if (path === '/gallery')
      return (
        // A written fallback rather than a spinner: this chunk is fetched on demand, so on a
        // slow connection the wait is real and a blank frame reads as a broken route.
        <Suspense fallback={<p className="web-shell__lazy">Loading the component gallery…</p>}>
          <Gallery />
        </Suspense>
      );
    /* `localMode ? undefined : orgId` rather than `orgId`: these two screens already branch on the
       org's absence internally, and an Entry/Sandbox client has an org while belonging on the local
       side of that branch. Passing the tier decision down as the absence of an org keeps ONE
       definition of "local" (above) instead of teaching each screen the tier model. */
    if (path === '/') return <Dashboard orgId={localMode ? undefined : orgId} onNavigate={navigate} />;
    /* ⚠️ Before `/sources`: exact-match routing means order does not strictly matter here, but the
       subpath-before-bare rule the Worker's routes follow is kept anyway so nobody "simplifies" this
       into a startsWith and reintroduces the fall-through. Reached from a connection's card on the
       Connect panel with `?connection=<id>` (FR-4.8, G2-7). */
    if (path === '/sources/custom-objects') return <CustomObjectsRoute />;
    if (path === '/sources') return <Sources orgId={localMode ? undefined : orgId} />;
    /* Local (PWA) mode. NOT in NAV, deliberately and for now: F15 makes local mode a *processing
       mode* rather than a destination, and ADR-065 routes an Entry/Sandbox client here — so the
       entry point belongs in the mode badge and the landing page's try-it path, not as an eighth
       nav item competing with the seven the prototype specifies. Reached by URL until that
       routing exists. */

    /* F11-A: the audit trail. Unlisted like the two above, and for a decided reason — see `nav.ts`.
       ⚠️ **Not tier-branched.** The hash-chained log this reads is the HOSTED plane's; a local-tier
       client has its own chain in IndexedDB and reaches it through the Reports screen. Showing a
       local client an empty hosted trail would be the "empty list standing in for no plane" defect
       one layer up, so the screen's own not_provisioned refusal is what they see. */
    /* ⚠️ **By TIER, like every other screen** — owner decision, 2026-08-06. This called the hosted
       `/v1/audit-events` unconditionally, so an installed client got a **generic fetch error**: a cloud
       screen failing rather than the local equivalent of it. The local trail was already there —
       `appendAudit`/`auditEvents` have existed since the local writeback landed, hash-chained by
       `crmApply` — and now has a screen. The comment this replaces claimed a local client "reaches it
       through the Reports screen"; Reports does show it, but a route named Audit log that errors is
       still a route that errors. */
    if (path === '/audit-log') return localMode ? <LocalAuditLog /> : <AuditLog orgId={orgId} />;
    /* F17: the quality scorecard. ⚠️ **By TIER**, like every screen since the 2026-08-06 owner
       decision — and here the local answer is a genuine architectural absence rather than a gap:
       [11 §6](../../../docs/platform/11-technical-architecture.md) makes scheduled and continuous
       monitoring unavailable locally by design, so there is no history to trend on a device that
       scores only when somebody opens it. That is stated as a quantified next step (IA 42's
       pattern), never as a bare disabled feature. The two sub-paths are the same screen's anchors,
       so they resolve here rather than falling through to the dashboard. */
    /* F25: what the cost bought. ⚠️ NOT tier-branched, and honestly so: the figure needs a rate the
       tenant configured and decisions the engine made, both of which a local session has — but the
       rate lives in the tenant plane, so a local client meets the screen's own no-organisation empty
       state, which says the same thing without pretending a second implementation exists. */
    if (path === '/value') {
      return <Value orgId={orgId} />;
    }
    if (path === '/quality' || path.startsWith('/quality/')) {
      return localMode ? <LocalQuality /> : <Quality orgId={orgId} />;
    }
    /* F18: the gate's surfaces. ⚠️ **Not tier-branched, and that is the honest answer rather than an
       omission.** [architecture.md §6](../../../docs/features/18-data-contracts-gateway/architecture.md)
       is explicit that lake gating is unavailable in local mode: there is no destination to gate, no
       hosted quality signal to reference, and no lake for an attestation to describe. The screen's own
       no-organisation empty state is what a local client meets, which says the same thing without
       pretending a local contract could exist. */
    if (path === '/contracts' || path.startsWith('/contracts/') || path === '/loads') {
      return <Contracts orgId={localMode ? undefined : orgId} />;
    }
    /* F19: the surface registry. ⚠️ Not tier-branched, for the same reason as F18 — a surface is
       published from decided state to a backend, and a device has neither. The screen's own
       no-organisation empty state is what a local client meets. */
    if (path === '/surfaces' || path.startsWith('/surfaces/')) {
      return <Surfaces orgId={localMode ? undefined : orgId} />;
    }
    /* F11-E/F11-F: people, roles and the safety defaults. Unlisted in the primary nav for the same
       reason as the audit trail — the prototype's seven destinations are the nav, and administration
       reaches the account menu. Here rather than on `settings.` because every write is audited into
       the tenant plane, which only this Worker can reach. */
    if (path === '/administration') {
      /* ⚠️ **By TIER, like every other screen** — owner decision, 2026-08-06. This was the ONE
         destination with no local variant, so an installed client navigating here met "No organisation
         in scope": a cloud screen refusing, rather than the local equivalent of it. The local screen is
         narrower for reasons rather than as a backlog (no team to administer, no plan metered, the wipe
         IS the closure) and it carries **IA 40's storage & wipe**, whose mechanism had only ever been
         called by tests. */
      if (localMode) return <LocalAdministration />;
      /* The org NAME travels too: F11-G's closure asks for it typed exactly, and a form that cannot
         show what to type would be a guessing game. */
      return <Administration orgId={orgId} orgName={active?.org_name} />;
    }
    /* Review and decide (IA 12/13/14). `startsWith`, because `#/matches/pairs/:id` is a real
       documented route that must resolve to this screen rather than falling through to the
       Dashboard — the trap `/local` hit before it was allow-listed. */
    /* By TIER (F03-B): the local screen reads the device store and applies through the local
       executor; a hosted tenant's pairs live in their plane and decisions must land there chained.
       One screen pretending to be both would blur the line the mode badge draws. */
    if (path.startsWith('/matches')) {
      /* B-174: `#/matches?object=contact`, the scorecard's drill-through. Read from the live hash
         rather than from `path`, which `routeFromHash` deliberately strips the query off. */
      const objectFilter = new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('object') ?? undefined;
      return localMode ? <Matches /> : <HostedMatches orgId={orgId} objectFilter={objectFilter} />;
    }
    /* ⚠️ **Z-3.** Was a placeholder for every client. Three of the nav's four sub-items are now real
       **in local mode**, built from the device's own store. A cloud client still gets the placeholder,
       because the hosted feed needs M3/M7 and showing them local findings would be answering a
       different question with data from the wrong place. */
    if (path === '/findings' && localMode) return <Findings onNavigate={navigate} />;
    /* F05-B: hosted findings' FIRST real content — the cross-system gap report, computed live from
       the plane. The wider M7 feed is still absent and the screen says so, but hiding a built
       report behind that honesty note would be the inverse defect. */
    if (path === '/findings') return <HostedFindings orgId={orgId} />;
    /* ⚠️ **Z-4.** Was a placeholder while `applyMergeLocally`/`rollbackMergeLocally` had shipped and
       were reachable only from an open cluster in `/matches` — so a merge could be applied and then
       there was nowhere to see that it had been. Local only, same reasoning as `/findings`: a cloud
       client's action queue is M6's and does not exist. */
    if (path === '/actions' && localMode) return <Actions onNavigate={navigate} />;
    /* ⚠️ **Z-5.** Was a placeholder while `buildCleanedCsv` existed and the only download lived on
       the run screen — an export that could only happen in the upload's own session. Local only,
       same reasoning as above: the hosted destination pipeline is M8's and does not exist. */
    /* ⚠️ **NOT `localMode`-gated any more, unlike its siblings.** A destination credential is held
       for the organisation in the relay — Plane A, the same place a source token lives — so it is
       the same capability in both tiers. It was gated, and the consequence was that the tier most
       likely to configure a real S3 bucket got a placeholder whose stated blocker ("destination
       credential capture, which needs auth") had just been built. Found on screen, signed in as a
       Managed Cloud client. The screen itself varies the copy; the register does not. */
    if (path === '/destinations') return <Destinations onNavigate={navigate} localMode={localMode} />;
    /* ⚠️ **Z-6.** Was a placeholder while the audit chain sat hash-verified in IndexedDB with
       nothing rendering it into a handable artifact — "compliance evidence" named data we held and
       never showed. Local only, same reasoning as above: the hosted report pipeline is M7's. */
    if (path === '/reports' && localMode) return <Reports onNavigate={navigate} />;
    /* ⚠️ **B-172.** The hosted half. Five routes shipped — `/v1/controls`, `/v1/compliance`,
       `/v1/gdpr-records`, `/v1/evidence-pack`, `/v1/dsar` — and a cloud client got a placeholder,
       so "compliance evidence" named data we held and never showed on BOTH tiers. Local mode keeps
       its own screen above: its chain has no external anchor and no retention expiry, so the two
       are different artefacts rather than one screen pretending to be both. */
    if (path === '/reports') return <Compliance orgId={orgId} />;
    const p = PLACEHOLDERS[path];
    return p ? <Placeholder {...p} /> : <Dashboard orgId={localMode ? undefined : orgId} onNavigate={navigate} />;
  }

  /* ⚠️ This early return must stay BELOW every hook, including `useQuery`
     and `useHelpDock` above — an earlier draft in `apps/settings` put an
     early return above a hook call and it changed the hook count exactly at
     the transition this code exists to handle. Any new hook goes above this
     line, not below it. */
  /* ── F22: the guest workspace, ABOVE every session gate ──────────────────────────────────────
     ⚠️ **The reader has no Dedupr account**, so checking a session would spin forever and then
     redirect them to a sign-in they cannot complete. Their credential is the token in the URL, which
     the route validates for itself. Placed here — below every hook, above every gate — for the same
     reason the comment above insists on: hook count must not change across this boundary. */
  if (routeFromHash(window.location.hash) === '/remediation' || window.location.hash.startsWith('#/remediation')) {
    return <Remediation />;
  }

  if (gate.kind === 'checking') {
    return (
      <div className="web-app__gate" role="status" aria-live="polite">
        <Spinner label="Checking your session" size="lg" />
      </div>
    );
  }
  if (gate.kind === 'redirecting') {
    return (
      <div className="web-app__gate" role="status" aria-live="polite">
        <Spinner label="Redirecting" size="lg" />
      </div>
    );
  }
  if (gate.kind === 'unreachable') {
    /**
     * ⚠️ **The offline launch (V-3). This branch is why the installed app is worth installing.**
     *
     * We could not ask whether this visitor is signed in, so the honest position is that we do not
     * know — and it must not be answered with either "signed out" (a claim we cannot support, which
     * would also throw away a live session on one flaky load) or "local tier" (a claim about their
     * *subscription*, which this tells us nothing about).
     *
     * What we do know is narrower and enough: **whatever is on this device is still usable.** The
     * matching engine chunk is pre-cached, the records are in IndexedDB, and neither needs us. So the
     * two screens that carry their own page headers — the same pair the signed-out gate exempts — are
     * served, with a notice that says what happened rather than a spinner that implies progress.
     *
     * ⚠️ The rest of the local surface (Dashboard, Findings, Actions, Destinations, Reports) renders
     * *inside the shell*, and the shell's mode pill and nav would have to assert a tier we cannot
     * establish while offline. Serving them here is real remaining value and a deliberately separate
     * decision — logged as V-3b rather than smuggled in, because getting the pill wrong is exactly
     * the defect (X-4) where the chrome guessed and contradicted the content.
     */
    const offlineNotice = (
      <div className="web-app__offline" role="status" aria-live="polite">
        <Badge variant="warning" dot>
          Offline
        </Badge>{' '}
        Dedupr could not be reached, so we cannot tell whether you are signed in. Everything already on
        this device still works, and this will clear itself when you are back online.
      </div>
    );
    /* ⚠️ `/matches` only — `/local` is gone (owner decision, 2026-08-06). Offline, the review surface
       still works because everything it reads is on this device. */
    if (path.startsWith('/matches')) {
      return (
        <>
          {offlineNotice}
          <Matches />
        </>
      );
    }
    /* Anywhere else: say so, and point at the screen that does work. Never the sign-in card — that
       would be asserting they are signed out, which is the thing we just failed to find out. */
    return (
      <div className="web-app__gate">
        {offlineNotice}
        <Placeholder
          title="You appear to be offline"
          description="Dedupr’s services could not be reached. Files you have already loaded on this device are still available, and matching runs here without a connection."
          owner="F15 / ADR-069"
          blockedOn="Nothing — reconnect, or open Sources to keep working with the files on this device."
        />
        <p>
          <a href="#/sources">Go to Sources</a>
        </p>
      </div>
    );
  }
  /**
   * ⚠️ **Z-9: EVERY route in local mode is behind the form — never just `/local`/`/matches`, and
   * never behind the *sign-in*.**
   *
   * Before the form existed, the sign-in card carried an unconditional "Try it now" button
   * (added in #44) straight to `/local` — a door with no lock: a visitor reached the whole local
   * product without telling us their work email, company, or use case, so the form collected
   * nothing from anyone who noticed the shorter path. `hasRequestedTrial()` (`isTrialPreview`
   * above) is the honest hinge that closes it, because the alternatives are both wrong:
   *   • Requiring a *session* would mean waiting for a magic-link email before you can open the
   *     file you came to try — and the local product needs nothing from us to run, so the wait
   *     would be ours to impose for no reason of ours.
   *   • Leaving it open collects nothing, which is the state Z-9 exists to end.
   *
   * ⚠️ **A second, later defect (found live, not in review): the gate was right and the REWARD on
   * the other side of it was wrong.** Once the form was submitted, only `/local` and `/matches`
   * were exempted from `<GatewaySignIn/>` — every other route (Findings, Actions, Destinations,
   * Reports, even the Dashboard) still showed the sign-in form again, and the two exempted screens
   * rendered bare, with no shell: no nav, no mode pill, none of "same menus, same everything,
   * except PWA not cloud" — a visitor who did exactly what was asked got a cut-down, upload-only
   * page instead of the product an *actually* signed-in Entry/Sandbox client sees for the identical
   * on-device work. The form is the gate; it must never also shrink what is on the other side of it.
   *
   * The fix: once `isTrialPreview` is true, this branch does not return early at all — control
   * falls through to the ordinary shell render below, with `localMode`/`tierIsLocal` now true for
   * exactly this reason, so the full nav (Load a file · Sources · Matches · Findings · Actions ·
   * Destinations · Reports) and every local screen render precisely as they do for `gate.kind ===
   * 'pwa'`. Not signed in still means not signed in — `session`/`orgId` stay absent, the account
   * menu shows "Account" rather than an email, and nothing here is a security boundary (unchanged
   * from Z-9's own warning below) — but functionality is not what "not signed in" should ration.
   */
  /* ⚠️ **No `isTrialPreview` escape any more.** A signed-out visitor in a web tab sees the sign-in
     card — there is no longer a path by which the web runs the on-device product. Signed out INSIDE the
     installed app is a different case and falls through, because the app is local mode's home. */
  if (gate.kind === 'signed_out' && !runningInstalled) {
    /* ⚠️ **It is a soft gate and must never be described as more.** Local mode is code running on
       someone else's machine; no gate here is a security boundary, and pretending otherwise would
       be believing a note on the door is a lock. What it does is make the form the ordinary way
       in, which is all it was ever for. */
    return (
      <GatewaySignIn
        auth={gatewayAuth!}
        /* ⚠️ **A link we cannot use must say so, not silently show the form again.** Supabase issues
           an *implicit-flow* link (`#access_token=…`) when the request did not originate here — a
           link forwarded to another browser, or requested by a script. ADR-069's session is
           Worker-mediated and accepts only the PKCE `?code=`, correctly: an access token in a
           fragment is exactly what that design keeps out of the browser. But the app simply ignored
           it, so someone who had just clicked a link they were sent landed on a plain sign-in page
           with no explanation and no reason to think anything had happened. Found by live testing. */
        notice={unusableCredential ? (
          <Callout tone="info" title="That link cannot be used here">
            It carried a sign-in token directly, which this app does not accept — a link has to be
            opened in the same browser that asked for it. Request a new one below and it will work.
          </Callout>
        ) : undefined}
        onSignedIn={(s) => void routeSession(s)}
        /* ⚠️ Still passed, but the component no longer renders an ungated button from it (Z-9).
           It now appears only in the `trial_queued` and `signup_sent` phases — i.e. **after** the
           form has been submitted — which is exactly the visitor it is for: someone who told us who
           they are and is now waiting on an email they do not need in order to start. */
        /* ⚠️ **An INSTALL, not a link into local mode** (owner decision, 2026-08-06). This was
           `tryItHref="#/local"` — a web link to the on-device product, which is exactly what must not
           exist. Offered only when the browser can actually install: a button that does nothing is
           worse than no button, and `canInstall()` is false wherever `beforeinstallprompt` never
           fired (iOS Safari among them, where the install is a Share-sheet action we cannot trigger). */
        onInstall={canInstall() ? () => void promptInstall() : undefined}
        onSignUp={submitSignUp}
        onStartTrial={startTrialSignup}
        /* The light block: this browser has asked for a trial before. Deliberately advisory — see
           `hasRequestedTrial`. */
        alreadyRequestedTrial={hasRequestedTrial()}
        verification={<div ref={turnstile.containerRef} />}
        turnstileToken={turnstile.token ?? ''}
        onTurnstileReset={turnstile.reset}
        /* 7 days, matching `customer_start_trial`'s default. Copy only — the database owns the
           real length, and passing it here rather than hardcoding it in the component keeps the
           sales-assisted 30-day free audit representable on the same tier. */
        selfServeTrialDays={7}
      />
    );
  }
  if (gate.kind === 'onboard') {
    return (
      <StartTrial
        email={gate.session.email}
        /* From the sign-up form, carried on the user record — see `GatewaySession.metadata`. */
        defaultOrgName={typeof gate.session.metadata.company === 'string' ? gate.session.metadata.company : ''}
        onStart={(details) => startTrialFor(gate.session, details)}
        onSignOut={() => {
          void gatewayAuth?.signOut().then(() => setGate({ kind: 'signed_out' }));
        }}
      />
    );
  }
  if (gate.kind === 'dedicated_unresolved') {
    return (
      <Placeholder
        title="Your dedicated workspace"
        description="Your organisation runs on a dedicated setup, routed to its own hostname rather than my.dedupr.cloud."
        owner="F12 / S6"
        blockedOn="deployment_record has no hostname column yet (ADR-065) — added when the first dedicated deployment is provisioned."
      />
    );
  }
  /* ⚠️ **`gate.kind === 'pwa'` has NO branch here, and that is the fix.** It had two before, each
     wrong in its own way: first a placeholder saying "the PWA is not built yet", which stopped being
     true the day local mode shipped; then a redirect to `#/local` followed by a spinner — and the
     gate never advanced past it, so the destination an Entry/Sandbox client was routed to after
     signing in was **an infinite spinner**. Found by tracing the post-form journey for Z-9.
     An Entry/Sandbox client belongs in the ordinary shell with the local surface active, exactly
     like a signed-out visitor trying the product — which is what `localMode` below expresses. */
  // gate.kind === 'stay' | 'pwa', or 'signed_out' inside the installed app — the ordinary shell below.

  return (
    /* M30 / F29-FR-25.8. ⚠️ **BR-10 requires user-facing times in the ORG's timezone**, and until
       this existed every `Timestamp` fell back to the browser's — so two colleagues saw different
       times for one audit event and neither was told why. `null` here means the organisation has
       set none, which the provider marks as `inferred` rather than presenting a guess as theirs. */
    <FormattingProvider timeZone={active?.timezone ?? null}>
      <AppShell
        items={items}
        activeId={activeNavId}
        /* The first sub-item IS the section's landing screen, so it is the active
           one whenever the section is open. Once the child screens exist they will
           each own their own route and this becomes a real lookup. */
        activeSubId={SUBNAV[activeRoute.id]?.[0]?.id}
        onHelpClick={help.onHelpClick}
        /* So the topbar circle announces aria-expanded — it is a disclosure, and
           the prototype toggles the panel from it rather than only opening. */
        helpOpen={help.helpOpen}
        brand={<BrandMark />}
        /* The org switcher lives at the top of the sidebar, as in the prototype —
           not as a labelled <select> in the topbar. `uiSize="md"` because the
           prototype's is 34px and this is a primary global control; it was
           rendering at the smallest available size (audit D-6). */
        sidebarHeader={
          memberships.length > 0 ? (
            <OrgSwitcher
              orgs={memberships.map((m) => ({
                id: m.org_id,
                name: m.org_name,
                meta: m.role,
              }))}
              currentId={memberships[orgIndex]?.org_id ?? memberships[0]!.org_id}
              onSelect={(id) => {
                const next = memberships.findIndex((m) => m.org_id === id);
                if (next >= 0) setOrgIndex(next);
              }}
            />
          ) : undefined
        }
        /* The same control in its rail form, so the collapsed rail still shows
           which organisation you are pointed at. Passed separately rather than
           reused: `collapsed` changes the trigger's shape, and the shell must not
           infer which form a caller's node is in. */
        sidebarHeaderCollapsed={
          memberships.length > 0 ? (
            <OrgSwitcher
              collapsed
              orgs={memberships.map((m) => ({
                id: m.org_id,
                name: m.org_name,
                meta: m.role,
              }))}
              currentId={memberships[orgIndex]?.org_id ?? memberships[0]!.org_id}
              onSelect={(id) => {
                const next = memberships.findIndex((m) => m.org_id === id);
                if (next >= 0) setOrgIndex(next);
              }}
            />
          ) : undefined
        }
        /* Global search — the prototype's topbar is built around this pill, and
           it belongs in the centre slot so it widens with the window. Input
           surface only: what it searches is F29 backlog, so it is deliberately
           not wired to a fake result list. */
        /* Docked beside the content column, so help and the screen it describes
           are readable together — the prototype's own arrangement (audit S-8). */
        /* Docked beside the content column, so help and the screen it describes are
           readable together — the prototype's own arrangement (audit S-8).

           Built by the shared `useHelpDock`, so all three product apps get one
           identical two-tab panel (Documentation · Ask Dedupr) instead of three
           hand-wired ones that had already diverged. */
        helpPanel={help.panel}
        topbarCentre={
          <SearchField
            value={query}
            onChange={setQuery}
            placeholder="Search records, findings, or action IDs"
            shortcut
            results={(() => {
              const q = query.trim().toLowerCase();
              if (!q) return [];
              const screens = NAV.flatMap((r) => [
                { id: r.id, kind: 'screen', tone: 'accent' as const, label: r.label, route: r.id },
                ...(SUBNAV[r.id] ?? []).map((sub) => ({
                  id: `${r.id}:${sub.id}`,
                  kind: 'screen',
                  tone: 'accent' as const,
                  label: `${r.label} · ${sub.label}`,
                  route: sub.route,
                })),
              ]).filter((s) => s.label.toLowerCase().includes(q));
              const sourceRows = (sources.data?.data ?? [])
                .filter((src) => (src.display_name ?? src.provider).toLowerCase().includes(q))
                .map((src) => ({
                  id: src.id,
                  kind: 'source',
                  tone: 'danger' as const,
                  label: src.display_name ?? src.provider,
                  meta: src.provider,
                  route: '/sources',
                }));
              return [...screens, ...sourceRows].slice(0, 8).map((r) => ({
                id: r.id,
                kind: r.kind,
                tone: r.tone,
                label: r.label,
                meta: 'meta' in r ? (r as { meta?: string }).meta : undefined,
                onSelect: () => {
                  setQuery('');
                  navigate(r.route);
                },
              }));
            })()}
          />
        }
        /* The prototype's topbar right-hand cluster, in its order: mode pill,
           notification bell, help, avatar. The mock-scenario switcher is NOT here
           — it is a development tool and now floats over the canvas instead of
           occupying designed chrome. */
        /* The mode pill is PASSIVE standing status, so it is the one piece of
           topbar chrome that may be dropped on a phone. At 375px the end cluster
           had ~194px of controls in 118px and the shell was ellipsising them,
           which rendered "Clo" beside a half-cut avatar. The mode is still
           reachable — the help panel names it, and it is not an action. */
        topbarEndOptional={
            /* ⚠️ **Was hardcoded to "Cloud", with the description "Running in Dedupr's cloud".** A
               local-mode visitor — whose records never leave their device — was being told the
               opposite of the truth by the one control whose entire job is to say where processing
               happens. It contradicted the privacy claim the product is sold on, on every screen.
               Found by looking at the deployed screenshot during Z-1, not by any test.
               ⚠️ Derived from `localMode`, NOT from `orgId` — corrected while fixing the Entry/Sandbox
               route. An Entry/Sandbox client HAS an organisation and still runs entirely on their own
               device, so `orgId` would have told the paying local-mode client "Cloud · Running in
               Dedupr's cloud": the same lie as before, in the other direction, to the one group whose
               contract is that their records never leave the machine. */
            <Tooltip
              content={
                localMode
                  ? 'Records are read and matched in this browser. Nothing is uploaded. Click to see both modes.'
                  : "Records are processed in your pinned region on Dedupr's infrastructure. Click to see both modes."
              }
            >
              <StatusPill
                tone={localMode ? 'info' : 'success'}
                description={localMode ? 'Running on this device' : "Running in Dedupr's cloud"}
                onClick={() => setModeModalOpen(true)}
                aria-expanded={modeModalOpen}
              >
                {localMode ? 'Local (PWA)' : 'Cloud'}
              </StatusPill>
            </Tooltip>
        }
        topbarEnd={
          <>
            {/* `StatusPill`, not `Badge`: a Badge is a 22px UPPERCASE row-level
                state, and rendering the mode through it made the topbar say
                "CLOUD" where the prototype says "Cloud" (audit S-4). It now
                opens the prototype's mode explainer (census `modeModalOpen`). */}
            {/* Hover explains, click opens the full picker — the prototype's
                `modeHover` tooltip and `modeModalOpen` modal respectively. */}

            <NotificationTray
              notifications={notifications}
              /* ⚠️ **This line said "a weekly digest goes to your inbox every Monday" while no digest
                 existed** — a claim the product did not honour, in the surface whose whole job is to
                 tell somebody what happened. It now names the setting that governs both channels
                 instead, which is true today. FR-11.2's digest is [B-165](../../docs/platform/TODO.md). */
              footer="Notification settings live under Administration — one choice covers email and this tray."
              onMarkAllRead={() => void markAllRead()}
            />

          </>
        }
        topbarTrail={
          <AvatarMenu
              /* `session.user.name` is nullable in the contract, so the email is
                 the fallback rather than a placeholder like "User" — an address
                 identifies who you are signed in as; a placeholder does not. */
              /* ⚠️ Optional on `user` too, not only on `data`. `session.data?.user.name` reads as
                 defensive and is not: the `?.` stops at `data`, so a response WITHOUT a `user`
                 throws on `.name` — during render, blanking the entire app with no console error a
                 reader would connect to an account menu. That is exactly what live mode did, because
                 `/v1/session` omitted the contract-required `user` and every mock supplied one.
                 The server is fixed; this makes the screen degrade to "Account" rather than vanish
                 if any future field goes missing. */
              name={session.data?.user?.name ?? session.data?.user?.email ?? 'Account'}
              email={session.data?.user?.email}
              role={
                memberships[orgIndex]
                  ? `${memberships[orgIndex]!.role} · ${memberships[orgIndex]!.org_name}`
                  : undefined
              }
            groups={accountMenu}
          />
        }
      >
        {trialEnded ? (
          <div className="web-app__trial-ended" role="status">
            <Badge variant="warning" dot>
              Trial ended
            </Badge>{' '}
            Your trial has ended. Everything on this device keeps working and nothing has been
            deleted — records here are yours, on your machine. To connect systems on a paid plan,{' '}
            <a href={`${surfaces.settings}/#/plan`}>choose a plan</a>.
          </div>
        ) : null}
        {unlisted ? null : <h1 className="web-app__title">{activeRoute.label}</h1>}
        {renderScreen()}
      </AppShell>

      {/* The mode explainer — the prototype's `modeModalOpen` modal, verbatim in
          structure: eyebrow, question title, two choice tiles, a strip note.

          ⚠️ **Corrected 2026-08-04 (V-17).** This said local mode was *"Not available yet — it
          arrives with the Entry/Sandbox tier (F15)"* and quoted *"~1–2M records"*, and both were
          false in the worst possible audience: the tile was shown, disabled, to Entry/Sandbox
          clients who were **using local mode at the time**, telling them the product they had open
          did not exist. The ceiling figure was the retired A-3 assumption that presumed
          DuckDB-WASM; the shipped engine enforces `MAX_RECORDS` and derives a per-file limit from
          the device (`capacity.ts`), which is why `lint:vocab` bans the old number in docs.

          Which tile reads as current now comes from `localMode`, the same fact the nav and the mode
          pill use — so the three cannot disagree, which is how X-4 happened. Neither tile is
          selectable: switching mode is a tier change (a `deployment_record`, ADR-065), not a
          browser preference, and a control implying otherwise would be the dead affordance Z-7
          exists to remove. */}
      <Modal
        open={modeModalOpen}
        onOpenChange={setModeModalOpen}
        eyebrow="Processing mode"
        title="Where should your records be matched?"
        subtitle="The same engine runs in both modes, so matches and findings are identical. What changes is where your records sit while the work happens."
        hint="Switching will not re-run anything you have already approved."
      >
        <div role="radiogroup" aria-label="Processing mode" className="web-app__mode-options">
          <ChoiceTile
            name="Cloud"
            selected={!localMode}
            tone="success"
            badge={!localMode ? <Badge variant="accent">Current</Badge> : undefined}
            meta="No record ceiling"
            blurb="Records are staged in your pinned region and processed on Dedupr's infrastructure."
            points={[
              { mark: 'yes', text: 'Scheduled syncs keep findings fresh without your browser open.' },
              { mark: 'yes', text: 'Multi-user review — teammates work the same queues.' },
              { mark: 'yes', text: 'Auto-merge can unlock once its precision gate is met.' },
            ]}
            disabled
          />
          <ChoiceTile
            name="Local (PWA)"
            selected={localMode}
            tone="info"
            badge={localMode ? <Badge variant="accent">Current</Badge> : undefined}
            /* ⚠️ No absolute figure here on purpose. The real limit is derived per file from this
               device's reported memory and the file's row width — a 28× spread between a 4-column
               and a 90-column export — so a single number printed in a modal would be wrong for
               most files. `LocalRun` states the limit and its basis for the file actually loaded,
               which is where the answer can be true. */
            meta="Sized to your device"
            blurb="Profiling and matching run on this device; records never reach a Dedupr server."
            points={[
              { mark: 'yes', text: 'Records stay on your own machine — the trust-building audit mode.' },
              { mark: 'yes', text: 'Works with no connection once installed; your files stay on the device.' },
              { mark: 'no', text: 'No scheduling, no multi-user review, no auto-merge tier.' },
            ]}
            disabled
          />
        </div>
      </Modal>

      {/* A floating development control, OUTSIDE the shell — it renders only when
          mocks are running, and it no longer occupies the topbar's designed space. */}
      <ScenarioSwitcher onChange={() => setDataNonce((n) => n + 1)} />
    </FormattingProvider>
  );
}
